import { useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, CircularProgress, LinearProgress, Tooltip } from '@mui/material';
import { Button, PageHeader } from 'Components/ui';
import { Main as Container } from 'Components/Main';
import ReactECharts from 'echarts-for-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getScannerStats,
  getScannerScans,
  getScannerTimeline,
  getScannerServices,
  getScannerAttackSurface,
  getScannerThreatScore,
  getScannerCompliance,
  getScannerServiceWatchlist,
  getScannerRegionMap,
  getScannerRiskRadar,
  getScannerRiskPipeline,
  initScannerTenant,
} from 'services/scanner-service';
import { ThreatMap } from './ThreatMap';
import { RiskRadar } from './RiskRadar';
import { RiskPipeline } from './RiskPipeline';
import { useAuthTenant } from 'store/auth';
import { resolveCssVar } from 'utils/css_var_utils';

// ── Theme ──────────────────────────────────────────────────────────────────────
const T = {
  bg: 'var(--vo-mist)',
  card: 'var(--vo-white)',
  cardBorder: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  textMuted: 'var(--vo-slate)',
  textDim: 'var(--vo-steel)',
};

// ── Severity colors ────────────────────────────────────────────────────────────
const SEV = {
  critical:      { color: 'var(--vo-sev-critical)', bg: 'var(--vo-danger-10)' },
  high:          { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  medium:        { color: 'var(--vo-sev-medium)', bg: 'var(--vo-warning-10)' },
  low:           { color: 'var(--vo-sev-low)', bg: 'var(--vo-sev-low-10)' },
  informational: { color: 'var(--vo-sev-info)', bg: 'var(--vo-info-8)' },
};
const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'informational'];

// ── Resource group icons (emoji) ───────────────────────────────────────────────
const GROUP_ICONS = {
  Container: '🐳', Monitoring: '📡', Storage: '🗄️', Security: '🔒',
  'AI/ML': '🤖', Database: '💾', IAM: '👤', Compute: '⚙️', Serverless: 'λ',
};

// ── Compliance framework badge ──────────────────────────────────────────────────
const FW_BADGE = (() => {
  const map = [
    { match: /^aws/i,      bg: '#FF9900', fg: '#000', label: 'AWS' },
    { match: /gdpr/i,      bg: '#003399', fg: 'var(--vo-white)', label: 'EU'  },
    { match: /hipaa/i,     bg: '#005EB8', fg: 'var(--vo-white)', label: 'HI'  },
    { match: /fedramp/i,   bg: '#1A2E5A', fg: 'var(--vo-white)', label: 'FR'  },
    { match: /gxp|gcp/i,   bg: '#6B21A8', fg: 'var(--vo-white)', label: 'GxP' },
    { match: /nist/i,      bg: '#0F4880', fg: 'var(--vo-white)', label: 'NI'  },
    { match: /iso/i,       bg: '#166534', fg: 'var(--vo-white)', label: 'ISO' },
    { match: /pci/i,       bg: '#B91C1C', fg: 'var(--vo-white)', label: 'PCI' },
    { match: /soc/i,       bg: '#0369A1', fg: 'var(--vo-white)', label: 'SOC' },
    { match: /cis/i,       bg: '#1D4ED8', fg: 'var(--vo-white)', label: 'CIS' },
    { match: /kisa/i,      bg: '#065F46', fg: 'var(--vo-white)', label: 'KI'  },
    { match: /mitre/i,     bg: '#7C3AED', fg: 'var(--vo-white)', label: 'MT'  },
    { match: /prowler/i,   bg: '#FF6B35', fg: 'var(--vo-white)', label: 'PW'  },
  ];
  return (fw) => {
    const m = map.find(e => e.match.test(fw));
    return m ?? { bg: '#475569', fg: 'var(--vo-white)', label: fw.slice(0,2).toUpperCase() };
  };
})();

// ── Shared card style ──────────────────────────────────────────────────────────
const card = (sx = {}) => ({
  backgroundColor: T.card,
  border: `1px solid ${T.cardBorder}`,
  borderRadius: 2,
  p: 2.5,
  ...sx,
});

// ── Fade-in animation ──────────────────────────────────────────────────────────
const Fade = ({ delay = 0, fill = true, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay }}
    style={fill ? { height: '100%' } : {}}
  >
    {children}
  </motion.div>
);

// ── Section title ──────────────────────────────────────────────────────────────
const SectionTitle = ({ children, action }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
    <Typography sx={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{children}</Typography>
    {action}
  </Box>
);

// ── Loading overlay ────────────────────────────────────────────────────────────
const Loading = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height={80}>
    <CircularProgress size={22} sx={{ color: 'var(--color-brand)' }} />
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
export const ScannerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('scanner');
  const selectedTenant = useSelector(useAuthTenant);
  const slug = selectedTenant?.slug;
  const [timelineDays, setTimelineDays] = useState(5);
  const [analysisTab, setAnalysisTab] = useState('threatmap');

  // ── SCAN STATE ────────────────────────────────────────────────────────────────
  const SCAN_COLORS = { executing: 'var(--vo-warning)', available: 'var(--vo-success)', completed: 'var(--vo-success)', failed: 'var(--vo-danger)', cancelled: 'var(--vo-slate)' };
  const SCAN_LABELS = {
    executing: t('scanner:scanStatus.executing'),
    available: t('scanner:scanStatus.available'),
    completed: t('scanner:scanStatus.completed'),
    failed: t('scanner:scanStatus.failed'),
    cancelled: t('scanner:scanStatus.cancelled'),
  };

  // Idempotent: creates the scanner org record for this company on first load
  useQuery(
    ['scannerTenantInit', slug],
    () => initScannerTenant(slug),
    { enabled: !!slug, staleTime: Infinity, retry: false },
  );

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['scannerStats', slug],
    () => getScannerStats(slug),
    { enabled: !!slug },
  );
  const { data: scansData } = useQuery(
    ['scannerScans', slug],
    () => getScannerScans(slug, 1, 5),
    { enabled: !!slug },
  );
  const { data: timelineData, isLoading: timelineLoading } = useQuery(
    ['scannerTimeline', slug, timelineDays],
    () => getScannerTimeline(slug, timelineDays),
    { enabled: !!slug },
  );
  const { data: servicesData } = useQuery(
    ['scannerServices', slug],
    () => getScannerServices(slug),
    { enabled: !!slug },
  );
  const { data: attackData } = useQuery(
    ['scannerAttack', slug],
    () => getScannerAttackSurface(slug),
    { enabled: !!slug },
  );
  const { data: threatScoreData, isLoading: threatScoreLoading } = useQuery(
    ['scannerThreatScore', slug],
    () => getScannerThreatScore(slug),
    { enabled: !!slug },
  );
  const { data: complianceData } = useQuery(
    ['scannerCompliance', slug],
    () => getScannerCompliance(slug),
    { enabled: !!slug },
  );
  const { data: serviceWatchlistData } = useQuery(
    ['scannerServiceWatchlist', slug],
    () => getScannerServiceWatchlist(slug),
    { enabled: !!slug, staleTime: 5 * 60 * 1000 },
  );
  const { data: regionMapData, isLoading: regionMapLoading } = useQuery(
    ['scannerRegionMap', slug],
    () => getScannerRegionMap(slug),
    { enabled: !!slug, staleTime: 5 * 60 * 1000 },
  );
  const { data: riskRadarData, isLoading: riskRadarLoading } = useQuery(
    ['scannerRiskRadar', slug],
    () => getScannerRiskRadar(slug),
    { enabled: !!slug, staleTime: 5 * 60 * 1000 },
  );
  const { data: riskPipelineData, isLoading: riskPipelineLoading } = useQuery(
    ['scannerRiskPipeline', slug],
    () => getScannerRiskPipeline(slug),
    { enabled: !!slug, staleTime: 5 * 60 * 1000 },
  );

  const stats      = statsData?.data || {};
  const scans      = scansData?.data?.scans || [];
  const timeline   = timelineData?.data || { labels: [], series: {} };
  const services   = servicesData?.data || [];
  const attack          = attackData?.data || [];
  const compliance      = complianceData?.data || [];
  const serviceWatchlist = Array.isArray(serviceWatchlistData?.data) ? serviceWatchlistData.data : [];
  const regionMap       = Array.isArray(regionMapData?.data) ? regionMapData.data : [];
  const riskRadar        = Array.isArray(riskRadarData?.data) ? riskRadarData.data : [];
  const riskPipeline     = riskPipelineData?.data ?? { nodes: [], links: [] };

  const totalFail   = stats.total || 0;
  const totalPass   = stats.passed || 0;
  const totalChecks = totalFail + totalPass;

  // Use Prowler-weighted ThreatScore (matches Prowler UI exactly) when available
  const threatScore   = threatScoreData?.data || {};
  const score         = threatScore.score ?? (totalChecks > 0 ? Math.round((totalPass / totalChecks) * 100) : 0);
  const scoreColor    = score >= 80 ? 'var(--vo-success)' : score >= 50 ? 'var(--vo-warning)' : 'var(--vo-danger)';
  const scoreLabel    = score >= 80 ? t('scanner:dashboard.secure') : score >= 50 ? t('scanner:dashboard.moderate') : t('scanner:dashboard.atRisk');
  const sections      = threatScore.sections || {};
  const gaps          = threatScore.gaps || [];

  // ── Gauge — half-circle, matches Prowler style ────────────────────────────
  const gaugeOption = {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 180, endAngle: 0,
      min: 0, max: 100,
      radius: '96%',
      center: ['50%', '72%'],
      progress: {
        show: true, roundCap: true, width: 20,
        itemStyle: { color: score > 0 ? resolveCssVar(scoreColor) : resolveCssVar('var(--vo-steel)') },
      },
      pointer: { show: false },
      axisLine: { roundCap: true, lineStyle: { width: 20, color: [[1, resolveCssVar('var(--vo-line)')]] } },
      axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
      detail: {
        valueAnimation: false,
        formatter: '{value}%',
        color: resolveCssVar(T.text), fontSize: 32, fontWeight: 700,
        offsetCenter: [0, '-20%'],
      },
      title: { offsetCenter: [0, '14%'], fontSize: 13, color: resolveCssVar(T.textMuted), fontWeight: 500 },
      data: [{ value: score, name: scoreLabel }],
    }],
  };

  // ── Donut ────────────────────────────────────────────────────────────────────
  const donutOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    series: [{
      type: 'pie', radius: ['44%', '70%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      label: { show: false }, emphasis: { label: { show: false } }, labelLine: { show: false },
      data: totalChecks > 0
        ? [
          { name: t('scanner:dashboard.fail'), value: totalFail, itemStyle: { color: resolveCssVar('var(--vo-danger)') } },
          { name: t('scanner:dashboard.pass'), value: totalPass, itemStyle: { color: resolveCssVar('var(--vo-success)') } },
        ]
        : [{ name: t('scanner:dashboard.noData'), value: 1, itemStyle: { color: resolveCssVar('var(--vo-line)') } }],
    }],
  };

  // ── Timeline ─────────────────────────────────────────────────────────────────
  const tlLabels = (timeline.labels || []).map(d => {
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  });
  const timelineOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: resolveCssVar('var(--vo-white)'), borderColor: resolveCssVar(T.cardBorder), textStyle: { color: resolveCssVar(T.text) } },
    legend: {
      bottom: 0, textStyle: { color: resolveCssVar(T.textMuted), fontSize: 11 },
      data: [
        t('scanner:severity.critical'),
        t('scanner:severity.high'),
        t('scanner:severity.medium'),
        t('scanner:severity.low'),
        t('scanner:severity.informational'),
      ],
    },
    grid: { top: 16, left: 48, right: 16, bottom: 50 },
    xAxis: {
      type: 'category', data: tlLabels,
      axisLine: { lineStyle: { color: resolveCssVar(T.cardBorder) } },
      axisLabel: { color: resolveCssVar(T.textMuted), fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: resolveCssVar(T.textMuted), fontSize: 11 },
      splitLine: { lineStyle: { color: resolveCssVar('var(--vo-line)') } },
    },
    series: [
      { name: t('scanner:severity.critical'),      type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: timeline.series?.critical || [], lineStyle: { color: resolveCssVar('var(--vo-sev-critical)') }, itemStyle: { color: resolveCssVar('var(--vo-sev-critical)') } },
      { name: t('scanner:severity.high'),           type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: timeline.series?.high     || [], lineStyle: { color: resolveCssVar('var(--vo-sev-high)') }, itemStyle: { color: resolveCssVar('var(--vo-sev-high)') } },
      { name: t('scanner:severity.medium'),         type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: timeline.series?.medium   || [], lineStyle: { color: resolveCssVar('var(--vo-sev-medium)') }, itemStyle: { color: resolveCssVar('var(--vo-sev-medium)') } },
      { name: t('scanner:severity.low'),            type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: timeline.series?.low      || [], lineStyle: { color: resolveCssVar('var(--vo-sev-low)') }, itemStyle: { color: resolveCssVar('var(--vo-sev-low)') } },
      { name: t('scanner:severity.informational'),  type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: timeline.series?.informational || [], lineStyle: { color: resolveCssVar('var(--vo-sev-info)') }, itemStyle: { color: resolveCssVar('var(--vo-sev-info)') } },
    ],
  };

  const navButtons = (
    <Box display="flex" gap={1}>
      {[
        [t('scanner:dashboard.nav.accounts'),  'accounts'],
        [t('scanner:dashboard.nav.findings'),  'findings'],
        [t('scanner:dashboard.nav.resources'), 'resources'],
        [t('scanner:dashboard.nav.scans'),     'scans'],
      ].map(([label, path]) => (
        <Button
          key={path}
          size="sm"
          variant="secondary"
          onClick={() => navigate(`/scanner/${path}`)}
          sx={{
            borderRadius: '8px', height: '36px', textTransform: 'none',
            fontSize: '0.8125rem', fontWeight: 500, px: 2,
            color: T.textMuted, borderColor: T.cardBorder,
            '&:hover': { color: T.text, borderColor: 'var(--color-brand)', backgroundColor: 'transparent' },
          }}
        >
          {label}
        </Button>
      ))}
    </Box>
  );

  if (statsLoading) {
    return (
      <Container>
      <PageHeader
        title={t('scanner:dashboard.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title') },
        ]}
        actions={navButtons}
      />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, py: 8 }}>
          <CircularProgress sx={{ color: 'var(--color-brand)' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('scanner:dashboard.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title') },
        ]}
        actions={navButtons}
      />
    <Box sx={{ mt: 3, pb: 6, color: T.text }}>

      {/* ── Row 1: ThreatScore | Check Findings | Risk Severity ─────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>

        {/* ThreatScore */}
        <Grid item xs={12} md={3}>
          <Fade delay={0.05}>
            <Tooltip
              placement="bottom"
              arrow
              disableHoverListener={threatScoreLoading || Object.keys(sections).length === 0}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'var(--vo-white)',
                    borderRadius: 2,
                    p: 1.5,
                    maxWidth: 290,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    border: '1px solid var(--vo-line)',
                  },
                },
                arrow: { sx: { color: 'var(--vo-white)', '&::before': { border: '1px solid var(--vo-line)' } } },
              }}
              title={
                <Box sx={{ minWidth: 220 }}>
                  {Object.entries(sections)
                    .sort(([, a], [, b]) => a - b)
                    .map(([sec, pct]) => {
                      const c = pct >= 80 ? 'var(--vo-success)' : pct >= 50 ? 'var(--vo-warning)' : 'var(--vo-danger)';
                      const label = pct % 1 === 0 ? `${pct.toFixed(1)}%` : `${pct}%`;
                      return (
                        <Box key={sec} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                          <Typography sx={{ fontSize: 11.5, color: 'var(--vo-slate)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {sec}
                          </Typography>
                          <Box sx={{ flex: 1, mx: 0.75, borderBottom: '1px dotted var(--vo-line)', minWidth: 12 }} />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: c, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  {gaps.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid var(--vo-line)', display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                      <Typography sx={{ fontSize: 13, flexShrink: 0 }}>⚠</Typography>
                      <Typography sx={{ fontSize: 11, color: 'var(--vo-slate)', lineHeight: 1.55 }}>
                        {t('scanner:dashboard.majorGaps', 'Major gaps include')}{' '}
                        {gaps.slice(0, 3).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            >
              <Box sx={card({ height: 280, cursor: Object.keys(sections).length > 0 ? 'default' : undefined })}>
                <SectionTitle>{t('scanner:dashboard.threatScore')}</SectionTitle>
                {threatScoreLoading ? (
                  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={22} sx={{ color: 'var(--color-brand)' }} />
                  </Box>
                ) : (
                  <Box sx={{ height: 200 }}>
                    <ReactECharts option={gaugeOption} style={{ height: 200 }} />
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Fade>
        </Grid>

        {/* Check Findings */}
        <Grid item xs={12} md={5}>
          <Fade delay={0.1}>
            <Box sx={card({ height: 280 })}>
              <SectionTitle>{t('scanner:dashboard.checkFindings')}</SectionTitle>
              <Grid container alignItems="center" sx={{ height: 220 }}>
                <Grid item xs={5}>
                  <Box sx={{ position: 'relative' }}>
                    <ReactECharts option={donutOption} style={{ height: 180 }} />
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, color: T.text, lineHeight: 1 }}>
                        {totalChecks.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: T.textMuted, mt: 0.3 }}>{t('scanner:dashboard.totalFindings')}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={7}>
                  <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      { label: t('scanner:dashboard.failFindings'), count: totalFail, color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)', border: 'var(--vo-danger-15)' },
                      { label: t('scanner:dashboard.passFindings'), count: totalPass, color: 'var(--vo-success)', bg: 'var(--vo-success-10)', border: 'var(--vo-success-20)' },
                    ].map(({ label, count, color, bg, border }) => (
                      <Box key={label} sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: bg, border: `1px solid ${border}` }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: 13, color, fontWeight: 600 }}>
                            {count.toLocaleString()} {label}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Grid>

        {/* Risk Severity */}
        <Grid item xs={12} md={4}>
          <Fade delay={0.15}>
            <Box sx={card({ height: 280 })}>
              <SectionTitle>{t('scanner:dashboard.riskSeverity')}</SectionTitle>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
                {SEV_ORDER.map(key => {
                  const count = stats[key] || 0;
                  const pct = totalFail > 0 ? Math.round((count / totalFail) * 100) : 0;
                  const s = SEV[key];
                  return (
                    <Box
                      key={key}
                      display="flex" alignItems="center" gap={1.5}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      onClick={() => navigate(`/scanner/findings?severity=${key}`)}
                    >
                      <Typography sx={{ fontSize: 12, color: T.textMuted, width: 68, flexShrink: 0 }}>
                        {t(`scanner:severity.${key}`)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          flex: 1, height: 7, borderRadius: 3,
                          backgroundColor: 'var(--vo-mist)',
                          '& .MuiLinearProgress-bar': { backgroundColor: s.color, borderRadius: 3 },
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: T.textMuted, width: 65, textAlign: 'right', flexShrink: 0 }}>
                        {`${pct}% · ${count.toLocaleString()}`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Fade>
        </Grid>
      </Grid>

      {/* ── Row 2: Resource Inventory ─────────────────────────────────────────── */}
      <Fade delay={0.2}>
        <Box sx={card({ mb: 2 })}>
          <SectionTitle
            action={
              <Typography
                sx={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/scanner/findings')}
              >
                {t('scanner:dashboard.viewAllResources')}
              </Typography>
            }
          >
            {t('scanner:dashboard.resourceInventory')}
          </SectionTitle>
          {services.length === 0 ? (
            <Loading />
          ) : (
            <Grid container spacing={1.5}>
              {services.map(({ group, label, failCount }) => (
                <Grid item xs={6} sm={4} md={3} key={group}>
                  <Box
                    sx={{
                      backgroundColor: 'var(--vo-mist)', border: `1px solid ${T.cardBorder}`,
                      borderRadius: 1.5, p: 1.5, cursor: 'pointer',
                      '&:hover': { borderColor: 'var(--color-brand)', backgroundColor: 'var(--vo-capri-50)' },
                    }}
                    onClick={() => navigate(`/scanner/findings?resourceGroup=${group}`)}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography sx={{ fontSize: 18 }}>{GROUP_ICONS[label] || '📦'}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t(`scanner:resourceGroups.${label}`, label)}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--vo-danger)' }} />
                      <Typography sx={{ fontSize: 12, color: 'var(--vo-danger)', fontWeight: 600 }}>
                        {failCount.toLocaleString()} {t('scanner:dashboard.failFindingsCount')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>

      {/* ── Row 3: Watchlists (left) + Attack Surface + Timeline (right) ───────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>

        {/* Left: Compliance + Service Watchlist */}
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Compliance Watchlist */}
            <Fade delay={0.25} fill={false}>
              <Box sx={card()}>
                <SectionTitle>{t('scanner:dashboard.complianceWatchlist', 'Compliance Watchlist')}</SectionTitle>
                {compliance.length === 0 ? <Loading /> : (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {compliance.slice(0, 8).map(({ framework, version, score }) => {
                      const badge = FW_BADGE(framework);
                      const scoreColor = score >= 80 ? 'var(--vo-success)' : score >= 50 ? 'var(--vo-warning)' : 'var(--vo-danger)';
                      const name = framework.replace(/-/g, ' ');
                      return (
                        <Box
                          key={`${framework}-${version}`}
                          display="flex" justifyContent="space-between" alignItems="center"
                          sx={{ py: 1, borderBottom: `1px solid ${T.cardBorder}`, '&:last-child': { borderBottom: 'none' } }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: 1, flexShrink: 0,
                              backgroundColor: badge.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: 8, fontWeight: 700, color: badge.fg, lineHeight: 1, textAlign: 'center' }}>
                                {badge.label}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: T.text, fontWeight: 500, lineHeight: 1.2,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                                {name}
                              </Typography>
                              {version && (
                                <Typography sx={{ fontSize: 9, color: T.textDim }}>{version}</Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: scoreColor, flexShrink: 0 }}>
                            {score}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Fade>

            {/* Service Watchlist */}
            <Fade delay={0.28} fill={false}>
              <Box sx={card()}>
                <SectionTitle>{t('scanner:dashboard.serviceWatchlist', 'Service Watchlist')}</SectionTitle>
                {serviceWatchlist.length === 0 ? <Loading /> : (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {serviceWatchlist.slice(0, 8).map(({ service, failCount }) => (
                      <Box
                        key={service}
                        display="flex" justifyContent="space-between" alignItems="center"
                        sx={{ py: 0.9, borderBottom: `1px solid ${T.cardBorder}`, '&:last-child': { borderBottom: 'none' } }}
                      >
                        <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500, fontFamily: 'monospace' }}>
                          {service}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--vo-danger)', fontVariantNumeric: 'tabular-nums' }}>
                          {failCount.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Fade>

          </Box>
        </Grid>

        {/* Right: Attack Surface + Timeline stacked */}
        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Attack Surface */}
            <Fade delay={0.25} fill={false}>
              <Box sx={card()}>
                <SectionTitle>{t('scanner:dashboard.attackSurface')}</SectionTitle>
                {attack.length === 0 ? <Loading /> : (
                  <Grid container spacing={1.5}>
                    {attack.map(({ key, label, count }) => (
                      <Grid item xs={6} sm={3} key={key}>
                        <Box sx={{
                          backgroundColor: 'var(--vo-mist)', border: `1px solid ${T.cardBorder}`,
                          borderRadius: 1.5, p: 1.5,
                        }}>
                          <Typography sx={{ fontSize: 28, fontWeight: 700, color: T.text, lineHeight: 1.1 }}>{count.toLocaleString()}</Typography>
                          <Typography sx={{ fontSize: 11, color: T.textMuted, mt: 0.5, lineHeight: 1.3 }}>{t(`scanner:attackSurface.${key}`, { defaultValue: label })}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Fade>

            {/* Findings Severity Over Time */}
            <Fade delay={0.3} fill={false}>
              <Box sx={card()}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography sx={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{t('scanner:dashboard.findingsOverTime')}</Typography>
                  <Box display="flex" gap={0.5}>
                    {[{ label: '5D', days: 5 }, { label: '7D', days: 7 }].map(({ label, days }) => (
                      <Button
                        key={label}
                        size="sm"
                        variant={timelineDays === days ? 'primary' : 'secondary'}
                        onClick={() => setTimelineDays(days)}
                        sx={{
                          minWidth: 40, height: 28, px: 1.5, borderRadius: '6px',
                          textTransform: 'none', fontSize: 11, fontWeight: 600,
                          ...(timelineDays === days
                            ? { backgroundColor: 'var(--color-brand)', borderColor: 'var(--color-brand)', color: 'white', '&:hover': { backgroundColor: 'var(--color-brand-strong)' } }
                            : { backgroundColor: 'var(--vo-mist)', borderColor: T.cardBorder, color: T.textMuted, '&:hover': { backgroundColor: 'var(--vo-line)', borderColor: T.cardBorder } }
                          ),
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>
                </Box>
                {timelineLoading ? <Loading /> : (
                  <>
                    <ReactECharts
                      option={timelineOption}
                      style={{ height: 340 }}
                      onEvents={{
                        legendselectchanged: (p) => navigate(`/scanner/findings?severity=${Object.keys(p.selected).find(k => p.selected[k]) ?? ''}`),
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: 'var(--color-brand)', cursor: 'pointer', mt: 0.5, '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => navigate('/scanner/findings')}
                    >
                      {t('scanner:dashboard.clickToFilter', 'Click to filter by severity')}
                    </Typography>
                  </>
                )}
              </Box>
            </Fade>

          </Box>
        </Grid>
      </Grid>

      {/* ── Row 4: Threat Map / Risk Radar (tabs, full width) ────────────────── */}
      <Fade delay={0.35} fill={false}>
        <Box sx={{ backgroundColor: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
          {/* Tab bar */}
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${T.cardBorder}`, px: 2, pt: 1.5 }}>
            {[
              { key: 'threatmap', label: t('scanner:dashboard.tabThreatMap', 'Threat Map') },
              { key: 'radar',     label: t('scanner:dashboard.tabRiskRadar', 'Risk Radar') },
              { key: 'pipeline',  label: t('scanner:dashboard.tabRiskPipeline', 'Risk Pipeline') },
            ].map(tab => (
              <Box
                key={tab.key}
                onClick={() => setAnalysisTab(tab.key)}
                sx={{
                  mr: 3, pb: 1.5, cursor: 'pointer',
                  borderBottom: analysisTab === tab.key ? '2px solid var(--color-brand)' : '2px solid transparent',
                  color: analysisTab === tab.key ? T.text : T.textMuted,
                  fontWeight: analysisTab === tab.key ? 600 : 400,
                  fontSize: '0.85rem',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': { color: T.text },
                }}
              >
                {tab.label}
              </Box>
            ))}
          </Box>

          {/* Content */}
          {analysisTab === 'threatmap' && (
            <ThreatMap regions={regionMap} isLoading={regionMapLoading} />
          )}
          {analysisTab === 'radar' && (
            <Box sx={{ p: 2 }}>
              <RiskRadar categories={riskRadar} isLoading={riskRadarLoading} slug={slug} />
            </Box>
          )}
          {analysisTab === 'pipeline' && (
            <Box sx={{ p: 2 }}>
              <RiskPipeline
                nodes={riskPipeline.nodes}
                links={riskPipeline.links}
                isLoading={riskPipelineLoading}
              />
            </Box>
          )}
        </Box>
      </Fade>

      {/* ── Row 5: Recent Scans ────────────────────────────────────────────────── */}
      <Fade delay={0.45}>
        <Box sx={card()}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography sx={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{t('scanner:dashboard.recentScans')}</Typography>
            <Typography
              sx={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/scanner/scans')}
            >
              {t('scanner:dashboard.viewAll')}
            </Typography>
          </Box>
          {scans.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 3, color: T.textMuted, fontSize: 13 }}>{t('scanner:dashboard.noScans')}</Typography>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    t('scanner:dashboard.colStartedAt'),
                    t('scanner:dashboard.colStatus'),
                    t('scanner:dashboard.colDuration'),
                  ].map(col => (
                    <th key={col} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textDim, borderBottom: `1px solid ${T.cardBorder}` }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scans.slice(0, 5).map(scan => {
                  const stateColor = SCAN_COLORS[scan.state] || 'var(--vo-slate)';
                  const stateLabel = SCAN_LABELS[scan.state] || scan.state;
                  const duration = scan.duration ? `${Math.round(scan.duration / 60)}m` : '—';
                  return (
                    <tr
                      key={scan.id}
                      style={{ cursor: 'pointer', borderBottom: `1px solid ${T.cardBorder}` }}
                      onClick={() => navigate(`/scanner/scans/${scan.id}`)}
                    >
                      <td style={{ padding: '8px 10px', fontSize: 12, color: T.textMuted }}>
                        {scan.started_at ? new Date(scan.started_at).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.2, borderRadius: 1, backgroundColor: `${stateColor}18`, border: `1px solid ${stateColor}40` }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: stateColor }} />
                          <Typography sx={{ fontSize: 10, color: stateColor, fontWeight: 600 }}>{stateLabel}</Typography>
                        </Box>
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: T.textMuted }}>{duration}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      </Fade>

    </Box>
    </Container>
  );
};
